import React, { useEffect } from 'react';

import { useTranslation } from 'react-i18next';
import { useMap } from 'react-map-gl';
import { useSelector, useDispatch } from 'react-redux';
import Stack from '@mui/material/Stack';
import { faBus } from '@fortawesome/free-solid-svg-icons';
import { GeocoderInput } from '../components/GeocoderInput';

import { CustomInputWithOutIcon } from 'handimap-react-lib/components/CustomInput';
import { setMultimodal } from 'handimap-react-lib/store/actions/profilActions';
import { setDirectionsDestination } from 'handimap-react-lib/store/actions/directionsActions';
import { setDirectionsOrigin } from 'handimap-react-lib/store/actions/directionsActions';
import { ScheduleSelector } from 'handimap-react-lib/components/ScheduleSelector';
import { CustomSwitcher } from 'handimap-react-lib/components/CustomSwitcher';

export const Directions = ({
  isMobile,
  multimodalFromUrl,
  multimodalActivated = true,
  timeFromUrl,
  styleSwitch,
}) => {
  const { t } = useTranslation();
  const { mymap } = useMap();
  const dispatch = useDispatch();

  const originInputValue = useSelector((state) => {
    return {
      label: state.directions.origin.label,
      value: state.directions.origin.marker,
    };
  });

  const destinationInputValue = useSelector((state) => {
    return {
      label: state.directions.destination.label,
      value: state.directions.destination.marker,
    };
  });

  const multimodalValue = useSelector((state) => state.profils.multimodal);

  const onSetOrigin = (label, center) => {
    dispatch(setDirectionsOrigin(center, label));
    if (Array.isArray(center) && center.length > 0) {
      mymap?.flyTo({ center: center, duration: 500, zoom: 15 });
    }
  };

  const onSetDestination = (label, center) => {
    dispatch(setDirectionsDestination(center, label));
    if (Array.isArray(center) && center.length > 0) {
      mymap?.flyTo({ center: center, duration: 500, zoom: 15 });
    }
  };

  const onSetMultimodal = (value) => {
    dispatch(setMultimodal(value));
  };

  useEffect(() => {
    if (multimodalFromUrl === 'true') {
      onSetMultimodal(true);
    } else if (multimodalFromUrl === 'false') {
      onSetMultimodal(false);
    }
  }, []);

  const styles = {
    container: {
      display: 'flex',
      width: '100%',
    },
    label: {
      whiteSpace: 'normal',
      color: '#000000',
    },
    inputField: {
      display: 'flex',
      width: '100%',
      backgroundColor: '#FFFFFF',
      '& input::placeholder': {
        fontSize: '0.8em',
        color: 'black',
        opacity: 0.6,
        paddingY: '1em',
      },
      outline: 'none',
      '& button': {
        visibility: 'visible',
      },
    },
  };

  return (
    <Stack sx={styles.container} direction='column' spacing={2}>
      <CustomInputWithOutIcon
        htmlTag='origin'
        id='origin-label'
        label={t('form.departure-label')}
        styleLabel={styles.label}
      >
        <GeocoderInput
          sx={styles.inputField}
          id='origin'
          labelId='origin-label'
          placeholder={
            isMobile
              ? t('form.mobile-placeholder-departure')
              : t('form.desktop-placeholder-departure')
          }
          onComplete={onSetOrigin}
          inputValue={originInputValue}
          ariaLabelledBy='originInput'
        />
      </CustomInputWithOutIcon>
      <CustomInputWithOutIcon
        htmlTag='destination'
        id='destination-label'
        label={t('form.arrival-label')}
        styleLabel={styles.label}
      >
        <GeocoderInput
          sx={styles.inputField}
          id='destination'
          labelId='destination-label'
          placeholder={
            isMobile
              ? t('form.mobile-placeholder-arrival')
              : t('form.desktop-placeholder-arrival')
          }
          onComplete={onSetDestination}
          inputValue={destinationInputValue}
          ariaLabelledBy='destinationInput'
        />
      </CustomInputWithOutIcon>
      {multimodalActivated && (
        <ScheduleSelector
          timeFromUrl={timeFromUrl}
          multimodal={multimodalValue}
        />
      )}
      {multimodalActivated && (
        <CustomSwitcher
          idSwitcher='switchTransport'
          label={t('form.transport-label')}
          icon={faBus}
          htmlTag='switchTransport'
          dataCy='switch-transport'
          defaultValue={multimodalValue}
          callBackFunction={onSetMultimodal}
          customStyle={styleSwitch}
        />
      )}
    </Stack>
  );
};
